import { CALLING_STAGES } from "../shifts";

/**
 * "Parte del día": what the setters will find today, sent every morning to
 * their WhatsApp group so they connect and sell.
 */
export const BRIEF_PATH = "/parte";

/** Black Friday 2026 runs until Monday 30 Nov 23:59; on 1 Dec prices go up. */
export const BLACK_FRIDAY_END = "2026-11-30";
export const OFFER_PRICE = 499;
export const PRICE_AFTER = 624;
export const COMMISSION_PER_SALE = 20;

export interface BriefLead {
  stage: string;
  sales_id?: unknown;
  lead_temperature?: string | null;
  recontact_at?: string | null;
  created_at: string;
}

export interface BriefRanker {
  first_name: string;
  points: number;
  sales: number;
}

export interface DailyBrief {
  day: string;
  dateLabel: string;
  /** Days left of Black Friday, today included (0 once it is over) */
  blackFridayDaysLeft: number;
  waiting: number;
  hot: number;
  recontactsToday: number;
  sureSales: number;
  newYesterday: number;
  monthSales: number;
  top: BriefRanker[];
  line: string;
}

/** YYYY-MM-DD of a date in Madrid. */
export const madridDay = (date: Date) =>
  date.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });

const dayNumber = (day: string) => Date.parse(`${day}T12:00:00Z`) / 86_400_000;

const shiftDay = (day: string, delta: number) =>
  new Date(Date.parse(`${day}T12:00:00Z`) + delta * 86_400_000)
    .toISOString()
    .slice(0, 10);

/** Lines of the day: one a day, in order. */
export const BRIEF_LINES = [
  "No vendes un aparato: le das a alguien la oportunidad de cambiar cómo bebe agua en casa.",
  "Sonríe al marcar, se nota en la voz: llamas de parte de una marca que cuida a sus clientes.",
  "Lo que compran es lo que reciben: el equipo, la instalación y una marca que los va a cuidar.",
  "Ellos pidieron información. No convences a nadie: les ayudas a decidir.",
  `«Me lo pienso» necesita una razón para hoy: el 1 de diciembre pagarán ${PRICE_AFTER - OFFER_PRICE} € más.`,
  "El que llama primero, vende. Lead nuevo: llámalo en menos de 5 minutos.",
  "Pregunta para qué la quieren: hijos, salud, comodidad. Vende eso, no el filtro.",
  "Un «ahora no» hoy puede ser un «sí» en la segunda llamada. Apúntalo y recontacta.",
  `Cada venta son ${COMMISSION_PER_SALE} € para ti. ¿Cuántas vas a cerrar hoy?`,
  "Agua buena en su grifo, sin cargar nada y sin plásticos: eso es lo que les cambias.",
];

export const lineOfDay = (day: string) =>
  BRIEF_LINES[
    ((Math.floor(dayNumber(day)) % BRIEF_LINES.length) + BRIEF_LINES.length) %
      BRIEF_LINES.length
  ];

export const blackFridayDaysLeft = (day: string) =>
  Math.max(0, Math.round(dayNumber(BLACK_FRIDAY_END) - dayNumber(day)) + 1);

const longDate = (day: string) => {
  const text = new Date(`${day}T12:00:00Z`).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const buildBrief = ({
  leads,
  ranking,
  now = new Date(),
}: {
  leads: BriefLead[];
  ranking: BriefRanker[];
  now?: Date;
}): DailyBrief => {
  const day = madridDay(now);
  const yesterday = shiftDay(day, -1);
  const calling = leads.filter((lead) => CALLING_STAGES.includes(lead.stage));
  const recontactsToday = leads.filter(
    (lead) =>
      lead.stage === "recontact" &&
      !!lead.recontact_at &&
      madridDay(new Date(lead.recontact_at)) <= day,
  ).length;
  const sureSales = leads.filter(
    (lead) => lead.stage === "sure_purchase",
  ).length;
  return {
    day,
    dateLabel: longDate(day),
    blackFridayDaysLeft: blackFridayDaysLeft(day),
    // Recontacts for a later day are not today's work
    waiting:
      calling.filter(
        (lead) =>
          lead.stage !== "recontact" ||
          !lead.recontact_at ||
          madridDay(new Date(lead.recontact_at)) <= day,
      ).length + sureSales,
    hot: calling.filter((lead) => lead.lead_temperature === "hot").length,
    recontactsToday,
    sureSales,
    newYesterday: leads.filter(
      (lead) => madridDay(new Date(lead.created_at)) === yesterday,
    ).length,
    monthSales: ranking.reduce((sum, row) => sum + (row.sales || 0), 0),
    top: [...ranking]
      .filter((row) => row.points > 0)
      .sort((a, b) => b.points - a.points)
      .slice(0, 3),
    line: lineOfDay(day),
  };
};

/** Text that goes with the image in the WhatsApp group. */
export const briefCaption = (brief: DailyBrief) =>
  [
    `☀️ *Parte del día · ${brief.dateLabel}*`,
    brief.blackFridayDaysLeft
      ? `🖤 Black Friday: quedan *${brief.blackFridayDaysLeft} ${brief.blackFridayDaysLeft === 1 ? "día" : "días"}* (${OFFER_PRICE} € con instalación; el 1/12 sube a ${PRICE_AFTER} €)`
      : null,
    `📞 *${brief.waiting} leads* esperando hoy${brief.hot ? ` · ${brief.hot} calientes` : ""}`,
    brief.recontactsToday
      ? `🔁 ${brief.recontactsToday} recontactos para hoy`
      : null,
    brief.sureSales ? `✅ ${brief.sureSales} leads en cierre de compra` : null,
    brief.newYesterday ? `🆕 ${brief.newYesterday} leads nuevos ayer` : null,
    "",
    `💬 ${brief.line}`,
    "",
    `👉 Conéctate y a llamar: crm.guladeagua.es · ${COMMISSION_PER_SALE} € por venta`,
  ]
    .filter((line) => line !== null)
    .join("\n");
